import type { QueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { AdminService } from '@/services/admin.service';
import { CompanyAuthService } from '@/services/company-auth.service';
import { ProfessionalsService } from '@/services/professional.service';
import { UserService } from '@/services/user.service';
import { clearUserScopedQueryCache } from '@/utils/clearUserScopedQueryCache';
import { clearSchedulingPayDraftSilently } from '@/utils/schedulingPayAfterAuth';
import {
	getCompanyData,
	getProfessionalData,
	getUserData,
	removeAdminData,
	removeCompanyData,
	removeProfessionalData,
	removeUserData,
} from '@/utils/cookies';

const tryLogout = async (logout: () => Promise<void>): Promise<void> => {
	try {
		await logout();
	} catch {
		// Sessão pode já ter expirado; seguimos com a limpeza local.
	}
};

export const hasLoggedInAccount = (): boolean =>
	Boolean(
		getUserData() ||
			getProfessionalData() ||
			getCompanyData() ||
			Cookies.get('admin_infos'),
	);

const clearAuthClientState = async (
	queryClient?: QueryClient,
): Promise<void> => {
	const hadUserSession = Boolean(getUserData());

	removeUserData();
	removeProfessionalData();
	removeAdminData();
	removeCompanyData();

	if (hadUserSession) {
		clearSchedulingPayDraftSilently();
	}

	if (!queryClient) return;

	await clearUserScopedQueryCache(queryClient);
	queryClient.removeQueries({ queryKey: ['professional-me'], exact: true });
	queryClient.removeQueries({ queryKey: ['admin-me'], exact: true });
	queryClient.removeQueries({ queryKey: ['company-me'], exact: true });
	queryClient.removeQueries({
		queryKey: ['forms', 'available', 'company', 'me'],
		exact: false,
	});
};

/** Encerra a sessão ativa antes de tentar login (só se já houver conta logada). */
export const logoutBeforeLoginIfNeeded = async (
	queryClient?: QueryClient,
): Promise<void> => {
	if (!hasLoggedInAccount()) return;

	const logoutTasks: Promise<void>[] = [];

	if (getUserData()) {
		logoutTasks.push(tryLogout(() => UserService.logout()));
	}
	if (getProfessionalData()) {
		logoutTasks.push(tryLogout(() => ProfessionalsService.logout()));
	}
	if (Cookies.get('admin_infos')) {
		logoutTasks.push(tryLogout(() => AdminService.logout()));
	}
	if (getCompanyData()) {
		logoutTasks.push(tryLogout(() => CompanyAuthService.logout()));
	}

	await Promise.all(logoutTasks);
	await clearAuthClientState(queryClient);
};

export const redirectToOAuthLogin = async (oauthUrl: string): Promise<void> => {
	await logoutBeforeLoginIfNeeded();
	window.location.href = oauthUrl;
};
